import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import {
  createUBusRelationshipGroup,
  updateUBusRelationshipGroupById,
  getUBusRelationshipGroupById,
} from "../../../../../services/uBusRelationshipGroup.service";
import {
  TRelationshipGroupForm,
  relationshipGroupSchema,
} from "./relationshipGroupSchema";
import {
  FormContainer,
  FormInput,
  FormTabs,
  SwitchInput,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { useNavigate, useParams } from "react-router-dom";
import { RelationshipGroupItemPage } from "./RelationshipGroupItem/RelationshipGroupItemPage";
import Commit from "@mui/icons-material/Commit";
import { UBusAutomation } from "./UBusAutomation";
import { useAppSelector } from "../../../../../store/store";

const translationPath = "page.4uBus.relationships.relationshipGroup.";

const RelationshipGroup = () => {
  const navigate = useNavigate();

  const { eI, extension, id } = useParams();

  const { t } = useTranslation();

  const currentPage = useAppSelector((state) => state.menu.actualPage?.name);

  const { setMessage } = useNotificationContext();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createUBusRelationshipGroup,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateUBusRelationshipGroupById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getUBusRelationshipGroupById,
  );

  const [code, setCode] = useState<number | null>(null);

  const [uBusAutomationCode, setUBusAutomationCode] = useState<number>();

  const disableAutomationEditExtensions = ["64", "71"];

  const saveHandler = (v: TRelationshipGroupForm) =>
    save({
      ...v,
      _4uBusAutomationId: v.uBusAutomation.id,
    });

  const updateHandler = (v: TRelationshipGroupForm, id: string) =>
    update({
      item: {
        ...v,
        _4uBusAutomationId: v.uBusAutomation.id,
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TRelationshipGroupForm>> => {
      const { data, success } = await get(id);
      if (data && success) {
        const newData: TRelationshipGroupForm = {
          ...data,
          uBusAutomation: {
            id: data._4uBusAutomation.id,
            name: data._4uBusAutomation.name,
          },
        };
        setCode(data.code);
        setUBusAutomationCode(data._4uBusAutomation.code);
        return { data: newData, success: true };
      }

      return { data, success };
    },
    [get],
  );

  const redirectOnSuccess = useCallback(
    (data?: any) => {
      if (!id) {
        if (data) {
          setMessage({
            message: t(translationPath + "created_successfully"),
            type: "success",
          });
          navigate(
            `/e/${eI}/4uBus/setRelationships/relationshipGroup/${extension}/${data.id}/edit`,
            { replace: true },
          );
        }
      } else {
        navigate(
          `/e/${eI}/4uBus/setRelationships/relationshipGroup/${extension}/`,
          { replace: true },
        );
      }
    },
    [navigate, setMessage, t, id, eI, extension],
  );

  const defineUBusAutomationCode = useCallback(
    (code: number) => setUBusAutomationCode(code),
    [],
  );

  const tabs = [
    {
      label: t(translationPath + "relationship_group_item"),
      icon: <Commit fontSize="small" />,
      component: (
        <RelationshipGroupItemPage uBusAutomationCode={uBusAutomationCode} />
      ),
    },
  ];

  const title = `${t(translationPath + "relationship")} ${
    currentPage ? t(currentPage) : ""
  }`;

  const loading = sLoading || uLoading || gLoading;

  return (
    <>
      <FormContainer<TRelationshipGroupForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={title}
        schema={relationshipGroupSchema}
        subtitle={t(translationPath + "new_relationship_group")}
        subtitleWatchField="name"
        redirectOnSave={false}
        onSuccess={redirectOnSuccess}
        formActionsOnBottom={false}
        marginBottom={5}
        customHeight="fit-content"
        fields={() => (
          <Grid container spacing={2}>
            {code && (
              <Grid item xs={12}>
                <Typography variant="body2" fontSize="1rem">
                  {`${t(translationPath + "code")}: ${code}`}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} textAlign="end">
              <SwitchInput
                name="enable"
                label={t(translationPath + "enable") || "Enable"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormInput
                size="small"
                name="name"
                fullWidth
                label={t(translationPath + "name") || "Name"}
              />
            </Grid>

            <UBusAutomation
              defineUBusAutomationCode={defineUBusAutomationCode}
              disableEdit={disableAutomationEditExtensions.includes(
                extension ?? "",
              )}
            />

            <Grid item xs={12}>
              <FormInput
                size="small"
                name="description"
                multiline
                rows={4}
                fullWidth
                label={t(translationPath + "description") || "Description"}
              />
            </Grid>
          </Grid>
        )}
      />

      {id && (
        <Grid container spacing={2} sx={{ width: "80%", margin: "0 auto" }}>
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default RelationshipGroup;
